import type { FC } from 'react';
import React, { useState, useEffect, useRef } from 'react';
import { cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';

const dragHandleStyles = cssMap({
	default: {
		width: '10px',
		height: '100%',
		top: '0',
		position: 'absolute',
		cursor: 'col-resize',
		borderLeft: `${token('border.width.selected')} solid transparent`,
		marginLeft: token('space.negative.150'),
		transition: 'border-color 0.2s ease-in-out',
		'&:hover': {
			borderColor: token('color.border.brand'),
		},
	},
	isDown: {
		borderColor: token('color.border.brand'),
	},
});

type RightPanelDragHandleProps = {
	onResizeDrag: (offset: number) => void;
	onResizeComplete: () => void;
};

type PreviousUpdate = {
	clientX: number;
	time: number;
};

export const RightPanelDragHandle: FC<RightPanelDragHandleProps> = ({
	onResizeDrag,
	onResizeComplete,
}) => {
	const handleRef = useRef<HTMLDivElement>(null);
	const [isDown, setIsDown] = useState(false);
	const previousUpdate = useRef<PreviousUpdate>({ clientX: 0, time: 0 });

	useEffect(() => {
		const downListener = (e) => {
			if (e.button !== 0) {
				return;
			}

			setIsDown(true);
			previousUpdate.current = {
				clientX: e.clientX,
				time: Date.now(),
			};
			document.body.style.cursor = 'col-resize';
			e.preventDefault();
		};

		const handleRefCurrent = handleRef.current;

		handleRefCurrent?.addEventListener('mousedown', downListener);

		return () => {
			document.body.style.cursor = 'default';
			handleRefCurrent?.removeEventListener('mousedown', downListener);
		};
	}, []);

	useEffect(() => {
		const upListener = (e) => {
			if (e.button !== 0) {
				return;
			}

			setIsDown(false);
			document.body.style.cursor = 'default';
			onResizeComplete();
		};

		const moveListener = (e) => {
			if (e.button !== 0) {
				return;
			}

			if (isDown) {
				const now = Date.now();
				if (now - previousUpdate.current.time > 30) {
					onResizeDrag(e.clientX - previousUpdate.current.clientX);
					previousUpdate.current = {
						clientX: e.clientX,
						time: now,
					};
				}
				e.preventDefault();
			}
		};

		window.addEventListener('mouseup', upListener);
		window.addEventListener('mousemove', moveListener);

		return () => {
			window.removeEventListener('mouseup', upListener);
			window.removeEventListener('mousemove', moveListener);
		};
	}, [isDown, onResizeDrag, onResizeComplete]);

	return (
		<div
			css={[dragHandleStyles['default'], dragHandleStyles['isDown']]}
			ref={handleRef}
			data-testid="resize-handle"
		/>
	);
};
